import type { ShiftClosing } from '@/types'
import {
  dispatchCustomerReceiptPrint,
  type CustomerReceiptSettings,
} from '@/lib/printCustomerReceipt'
import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import { escapeHtml } from '@/lib/printReport'
import { formatDateHuman, formatTimeHuman } from '@/lib/utils'

/**
 * Shift report — the printable handover slip for one shift close (F-CTRL-N29).
 * The X-report sibling of the Z report: a mid-day drawer reconciliation that a
 * shift lead prints and signs at handover. 80mm thermal, monochrome, through
 * the same off-screen iframe as the Z report / customer receipt. The Z report
 * remains the single legal end-of-day document; this is an operational slip.
 */
export function printShiftReport(
  shift: ShiftClosing,
  businessDate: string,
  cfg: CustomerReceiptSettings,
  formatCurrency: (n: number) => string,
): { printed: boolean } {
  const html = buildShiftReportHtml(shift, businessDate, cfg, formatCurrency)
  return { printed: dispatchCustomerReceiptPrint(html) }
}

function buildShiftReportHtml(
  sh: ShiftClosing,
  businessDate: string,
  cfg: CustomerReceiptSettings,
  fc: (n: number) => string,
): string {
  const v = sh.cash_variance
  const varianceLabel =
    Math.abs(v) < 0.01 ? 'Balanced' : v < 0 ? `${fc(Math.abs(v))} short` : `${fc(v)} over`
  const varianceCls = Math.abs(v) < 0.01 ? 'sum' : 'sum-flag'

  const hdr = `
    <div class="hdr">
      ${cfg.logoUrl ? `<img class="logo" src="${escapeHtml(cfg.logoUrl)}" alt="" />` : ''}
      <div class="biz">${escapeHtml(cfg.businessName || 'Restaurant')}</div>
      ${cfg.address ? `<div class="meta">${escapeHtml(cfg.address)}</div>` : ''}
      ${cfg.phone ? `<div class="meta">${escapeHtml(cfg.phone)}</div>` : ''}
    </div>

    <div class="title">SHIFT REPORT &mdash; SHIFT ${sh.shift_number}</div>
    <div class="zhead">
      <div><span class="lbl">Business date</span><span class="val">${escapeHtml(formatDateHuman(businessDate))}</span></div>
      <div><span class="lbl">Closed by</span><span class="val">${escapeHtml(nameOrDash(sh.closed_by_name))}</span></div>
      <div><span class="lbl">Shift window</span><span class="val">${escapeHtml(formatTimeHuman(sh.window_start))} – ${escapeHtml(formatTimeHuman(sh.closed_at))}</span></div>
    </div>
  `

  const sales = `
    <div class="section">
      <div class="section-title">This shift's sales</div>
      <table class="kv">
        ${kvRow('Cash', escapeHtml(fc(sh.shift_cash_sales)))}
        ${kvRow('Card', escapeHtml(fc(sh.shift_card_sales)))}
        ${kvRow('Online', escapeHtml(fc(sh.shift_online_sales)))}
        ${kvRow('Gross sales', escapeHtml(fc(sh.shift_gross_sales)), 'sum')}
        ${kvRow('Orders', String(sh.shift_order_count))}
      </table>
    </div>
  `

  const drawer = `
    <div class="section">
      <div class="section-title">Cash drawer</div>
      <table class="kv">
        ${kvRow('Opening float', escapeHtml(fc(sh.opening_cash)))}
        ${kvRow('+ Cash this shift', escapeHtml(fc(sh.shift_cash_sales)))}
        ${kvRow('Expected in drawer', escapeHtml(fc(sh.expected_cash)), 'sum')}
        ${kvRow('Counted cash', escapeHtml(fc(sh.counted_cash)))}
        ${kvRow('Variance', varianceLabel, varianceCls)}
        ${sh.cash_drop > 0 ? kvRow('Moved to safe', escapeHtml(fc(sh.cash_drop))) : ''}
        ${sh.cash_drop > 0 ? kvRow('Carried to next shift', escapeHtml(fc(sh.counted_cash - sh.cash_drop)), 'sum') : ''}
      </table>
    </div>
  `

  const cardOnline =
    sh.counted_card != null || sh.counted_online != null
      ? `
    <div class="section">
      <div class="section-title">Counted (card / online)</div>
      <table class="kv">
        ${sh.counted_card != null ? kvRow('Card machine', escapeHtml(fc(sh.counted_card))) : ''}
        ${sh.counted_online != null ? kvRow('Online / wallet', escapeHtml(fc(sh.counted_online))) : ''}
      </table>
    </div>`
      : ''

  const signoff = `
    <div class="section">
      <div class="section-title">Handover</div>
      ${sh.notes ? `<div class="notes">${escapeHtml(sh.notes)}</div>` : ''}
      <div class="signline">Outgoing: ____________________</div>
      <div class="signline">Incoming: ____________________</div>
    </div>
  `

  const footer = `
    <div class="footer">
      <div>Shift ${sh.shift_number} · closed ${escapeHtml(formatTimeHuman(sh.closed_at))} · ${escapeHtml(nameOrDash(sh.closed_by_name))}</div>
      <div class="attr">software powered by bhookly.com</div>
    </div>
  `

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Shift ${sh.shift_number} Report</title>
<style>${stylesheet(cfg)}</style>
</head><body>
<div class="paper">
  ${hdr}
  ${sales}
  ${drawer}
  ${cardOnline}
  ${signoff}
  ${footer}
</div>
</body></html>`
}

function kvRow(
  label: string,
  value: string,
  variant: 'plain' | 'sum' | 'sum-flag' = 'plain',
): string {
  const cls = variant === 'sum' ? 'row sum' : variant === 'sum-flag' ? 'row sum flag' : 'row'
  return `<tr class="${cls}"><td>${escapeHtml(label)}</td><td class="num">${value}</td></tr>`
}

function nameOrDash(v: string | null | undefined): string {
  if (!v) return '—'
  return v
}

function stylesheet(cfg: CustomerReceiptSettings): string {
  const baseCss = thermalPrintBaseCss({
    paperWidthMm: cfg.paperWidthMm,
    printableAreaMm: cfg.printableAreaMm,
    fontScale: cfg.fontScale,
  })
  const w = cfg.printableAreaMm
  const scale = cfg.fontScale
  return `
    ${baseCss}
    body {
      font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
      font-size: calc(11px * ${scale});
      font-weight: 700;
      line-height: 1.35;
    }
    .paper { width: ${w}mm; max-width: ${w}mm; margin: 0 auto; padding: 2mm 2mm 5mm; box-sizing: border-box; }
    .hdr { text-align: center; }
    .logo { max-width: 70%; max-height: 28mm; object-fit: contain; margin: 0 auto 2mm; display: block; }
    .biz { font-size: calc(16px * ${scale}); font-weight: 900; letter-spacing: 0.02em; margin-bottom: 1mm; }
    .meta { font-size: calc(10px * ${scale}); color: #000; }
    .title { text-align: center; font-weight: 900; font-size: calc(13px * ${scale}); margin: 3mm 0 1mm; letter-spacing: 0.05em; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 1mm 0; }
    .zhead { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5mm 3mm; margin: 1mm 0 0; font-size: calc(10px * ${scale}); }
    .zhead .lbl { color: #444; margin-right: 1mm; }
    .zhead .val { font-weight: 800; }
    .section { margin-top: 3mm; }
    .section-title { font-weight: 900; font-size: calc(11px * ${scale}); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #000; padding-bottom: 0.5mm; margin-bottom: 1mm; }
    table { width: 100%; border-collapse: collapse; }
    table.kv td { padding: 0.4mm 0; vertical-align: top; font-size: calc(11px * ${scale}); }
    table.kv td:last-child { text-align: right; font-variant-numeric: tabular-nums; padding-right: 1mm; }
    .row.sum td { border-top: 1px dashed #000; padding-top: 0.8mm; font-weight: 800; }
    .row.sum.flag td:last-child { font-weight: 900; text-decoration: underline; }
    .notes { font-style: italic; font-size: calc(10.5px * ${scale}); padding: 1mm 0; border-left: 2px solid #000; padding-left: 2mm; margin-bottom: 2mm; }
    .signline { margin-top: 4mm; font-size: calc(11px * ${scale}); }
    .footer { margin-top: 4mm; padding-top: 1mm; border-top: 1px dashed #000; text-align: center; font-size: calc(9.5px * ${scale}); color: #333; }
    .footer .attr { margin-top: 1mm; font-style: italic; }
  `
}
